import React, { useState, useEffect, useCallback, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import { Link } from 'react-router-dom';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
// import SurgeryFilters from './SurgeryFilters';
import { StatusBadge, PathwayBadge, SURGERY_STATUS } from '../shared';
import {
  BILLING_STATUS,
  BILLING_STATUS_PRIMARY_ACTION,
  isBillingCleared,
} from '../shared/constants';
import { surgeryService } from '../../../services/surgeryService';
import { history } from '../../../Helpers/history';

import Toolbar from "@material-ui/core/Toolbar";
import clsx from "clsx";
import InputTextField from "../../Utils/FormInputs/TextField";
import { useToolbarStyles } from "../../Accounting/FixedAssets/FixedAssetList";
import {
  CustomTableCell,
  MuiPaper,
  StickyHeadTable,
} from "../../Utils/Tables/StickyHeaderTable";
import { CustomTablePagination } from "../../Utils/Tables/CustomTable";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 22,
    padding: 20,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  titleText: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#333',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
  },
  emptyState: {
    textAlign: 'center',
    padding: 60,
    color: '#999',
  },
  actionLink: {
    textDecoration: 'none',
  },
}));

const INITIAL_FILTERS = {
  status: '',
  pathway: '',
  operation_class: '',
  date_from: '',
  date_to: '',
  search: '',
};

const normalizeSurgeryRow = (row) => ({
  id: row.id,
  patient_name: `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.patient_number || 'Unknown',
  patient_number: row.patient_number,
  procedure: row.service_name || row.procedure || '-',
  status: Number.isFinite(Number(row.status)) ? Number(row.status) : row.status,
  pathway: row.pathway || '',
  operation_class: row.operation_class || '',
  operation_date: row.operation_date || '',
  estimated_start_time: row.estimated_start_time || '',
  theatre_name: row.theatre_name || row.theatre || '-',
  lead_surgeon_name: row.lead_surgeon || '',
  lead_surgeon_type: row.lead_surgeon_type || '',
  patient_admission_id: row.patient_admission_id || null,
  billing_status: row.billing_status || null,
  is_on_insurance: row.is_on_insurance || 0,
  insurance_provider_name: row.insurance_provider_name || null,
  all_bills_paid: row.all_bills_paid || false,
});

const SurgeryListPage = () => {
  const classes = useStyles();

  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });
  const [searchInput, setSearchInput] = useState('');
  const [surgeries, setSurgeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const searchDebounceRef = useRef(null);

  const fetchSurgeries = useCallback(async () => {
    setLoading(true);
    try {
      const apiPage = page + 1; // API is 1-indexed
      const res = await surgeryService.listSurgeries(apiPage, {
        status: filters.status !== '' ? filters.status : undefined,
        search: filters.search || undefined,
        operation_class: filters.operation_class || undefined,
        start_date: filters.date_from || undefined,
        end_date: filters.date_to || undefined,
        per_page: rowsPerPage,
      });
      const responseData = res.data || {};
      let surgeriesData = [];
      if (Array.isArray(responseData)) {
        surgeriesData = responseData;
        setTotal(surgeriesData.length);
      } else if (Array.isArray(responseData.surgeries)) {
        surgeriesData = responseData.surgeries;
        setTotal(responseData.total_rows || responseData.total || surgeriesData.length);
      } else {
        surgeriesData = [];
        setTotal(0);
      }
      setSurgeries(surgeriesData.map(normalizeSurgeryRow));
     } catch (err) {
       console.error('Failed to load surgeries', err);
       setSurgeries([]);
       setTotal(0);
     } finally {
       setLoading(false);
     }
   }, [page, rowsPerPage, filters]);

  useEffect(() => {
    fetchSurgeries();
  }, [fetchSurgeries]);

  // ── Filter handlers ──────────────────────────────────────────────────────
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({ ...INITIAL_FILTERS });
    setSearchInput('');
    setPage(0);
  };

  // ── Pagination ───────────────────────────────────────────────────────────
  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      handleFilterChange('search', val);
    }, 300);
  };

  const toolbarClasses = useToolbarStyles ? useToolbarStyles() : {};

  const paginated = surgeries;

  const headData = [
    { id: "patient", label: "Patient" },
    { id: "procedure", label: "Procedure" },
    { id: "status", label: "Status" },
    { id: "pathway", label: "Pathway" },
    { id: "date", label: "Date" },
    { id: "theatre", label: "Theatre" },
    { id: "actions", label: "Actions" },
  ];

  const pagination = (
    <CustomTablePagination
      colSpan={3}
      page={page}
      rowsPerPage={rowsPerPage}
      count={total > 0 ? total : surgeries.length}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      isTotalCount
    />
  );

  // ── Row click in table view ──────────────────────────────────────────────
  const handleRowClick = (id) => {
    history.push(`/surgery/detail/${id}`);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      <PageTitle title="Surgery List" />
      <SubHeader title="Surgery" subTitle="Surgery List" />

      <div className={classes.root}>
        {/* Filters temporarily removed */}

        {/* Loading */}
        {loading && (
          <div className={classes.loadingWrap}>
            <CircularProgress />
          </div>
        )}

        {/* Empty State */}
        {!loading && surgeries.length === 0 && (
          <Paper className={classes.emptyState} elevation={0}>
            <Typography variant="h6" color="textSecondary">
              No surgeries found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Try adjusting your filters
            </Typography>
          </Paper>
        )}

        {/* Table View */}
        {!loading && (
          <MuiPaper>
            <Toolbar className={clsx(toolbarClasses.root)}>
              <div className="col-md-4 col-lg-4 col-sm-12" style={{ paddingLeft: 0 }}>
                <InputTextField
                  onChange={handleSearchChange}
                  type="text"
                  value={searchInput}
                  name="search"
                  id="searchValue"
                  placeholder="Search patient name or number..."
                />
              </div>
              <div className={toolbarClasses.spacer}></div>
            </Toolbar>
            <StickyHeadTable
              width="100%"
              pagination={pagination}
              data={paginated}
              columns={headData}
            >
              {surgeries.length > 0 ? (
                <>
                  {paginated.map((s) => {
                    const billingCleared = isBillingCleared(s.billing_status);
                    const billingAction = BILLING_STATUS_PRIMARY_ACTION[s.billing_status];
                    // Cash day-case is unpaid only if billing_status says so AND bills haven't been paid through the billing list
                    const isCashDayCaseUnpaid = !s.is_on_insurance && s.billing_status === BILLING_STATUS.CASH_UNPAID && s.pathway === 'outpatient' && !s.all_bills_paid;

                    return (
                      <TableRow
                        key={s.id}
                        className={classes.tableRow}
                        onClick={() => handleRowClick(s.id)}
                      >
                        <CustomTableCell>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {s.patient_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {s.patient_number}
                          </Typography>
                        </CustomTableCell>
                        <CustomTableCell>{s.procedure}</CustomTableCell>
                        <CustomTableCell>
                          <StatusBadge status={s.status} />
                        </CustomTableCell>
                        <CustomTableCell>
                          {s.pathway && <PathwayBadge pathway={s.pathway} />}
                        </CustomTableCell>
                        <CustomTableCell>
                          {s.operation_date || '-'}
                          {s.estimated_start_time
                            ? ` ${s.estimated_start_time}`
                            : ''}
                        </CustomTableCell>
                        <CustomTableCell>{s.theatre_name || '-'}</CustomTableCell>
                        <CustomTableCell onClick={(e) => e.stopPropagation()}>
                          <button
                            className="btn btn-sm sms-btn mr-1"
                            onClick={(e) => { e.stopPropagation(); history.push(`/surgery/detail/${s.id}`); }}
                          >
                            View
                          </button>
                        </CustomTableCell>
                      </TableRow>
                    );
                  })}
                </>
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={headData.length}>
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </StickyHeadTable>
          </MuiPaper>
        )}
      </div>
    </div>
  );
};

export default SurgeryListPage;
